import { PageShell, Section } from "@/components/PageShell";

const mail = (
  <a href="mailto:support@commandcenteradhd.com" className="font-semibold text-[#A8D5F5] hover:text-white">
    support@commandcenteradhd.com
  </a>
);

export default function Privacy() {
  return (
    <PageShell
      title="Privacy Policy"
      testid="privacy-page"
    >
      <div className="space-y-2" data-testid="privacy-intro">
        <p className="text-white">
          <strong>CommandCenterADHD</strong> (website and the &ldquo;Command Center&rdquo; app for iOS and Android)
        </p>
        <p className="font-semibold text-white" data-testid="privacy-effective-date">Effective date: September 23, 2026</p>
        <p>
          CommandCenterADHD is operated by Eagle Africa Trading and Distribution Corporation, Charlotte, North
          Carolina, USA (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). This policy explains what we collect
          through commandcenteradhd.com and the Command Center app, why we collect it, and the choices you have.
        </p>
      </div>

      <Section heading="1. Information we collect">
        <p className="font-semibold text-white">Information you give us</p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-white">Email address</strong>, when you request the free planner, join the community, make a purchase, or sign in.</li>
          <li><strong className="text-white">Planner content</strong> you enter in the Today view: your one priority, quick wins, and time blocks.</li>
          <li><strong className="text-white">Community content</strong> you post, such as posts, comments, and reactions.</li>
          <li><strong className="text-white">Support messages</strong> you send us.</li>
        </ul>
        <p className="font-semibold text-white">Purchase information</p>
        <p>
          Payments are processed by PayPal. We receive and keep order details: your email, the product purchased,
          the amount, and the date. We never receive or store your full card or bank details.
        </p>
        <p className="font-semibold text-white">Information collected automatically</p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-white">Website:</strong> basic usage information such as pages visited and actions taken. Analytics and marketing tools (Meta, Google, TikTok) load only after you accept them in the cookie banner. If you decline, they do not load. You can change your choice at any time by clearing your saved cookie preference.</li>
          <li><strong className="text-white">App:</strong> a sign-in token stored on your device so you stay logged in, and standard technical information sent with each request (such as IP address and device or browser type) that our servers use to operate and secure the service.</li>
        </ul>
        <p>
          You do not need to share any diagnosis or health information to use CommandCenterADHD, and we do not ask for it.
        </p>
      </Section>

      <Section heading="2. How we use your information">
        <ul className="list-disc space-y-2 pl-5">
          <li>Deliver the free planner and purchase access, and sign you in with emailed login links or 6-digit codes.</li>
          <li>Save and sync your planner content across your devices.</li>
          <li>Operate the community.</li>
          <li>Send onboarding messages, support replies, and occasional product updates. You can unsubscribe from marketing emails at any time.</li>
          <li>Keep the service secure, prevent abuse, and improve it.</li>
        </ul>
        <p>
          We do not sell or share your personal information for cross-context behavioral advertising, and we do not
          use your planner content for advertising.
        </p>
      </Section>

      <Section heading="3. The mobile app">
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-white">No tracking.</strong> The app contains no advertising or third-party analytics SDKs and does not track you across other companies&apos; apps or websites.</li>
          <li><strong className="text-white">Reminders stay on your device.</strong> Daily and time-block reminders are off by default. If you turn them on, they are scheduled locally on your phone with your permission. You can turn them off in the app&apos;s Settings or in your device settings.</li>
          <li><strong className="text-white">Offline saving.</strong> If you edit while offline, your changes are kept on your device until they sync.</li>
          <li><strong className="text-white">No in-app purchases.</strong> The app does not process payments.</li>
        </ul>
      </Section>

      <Section heading="4. Service providers">
        <p>
          We share information only with vendors that help us run the service: payment processing (PayPal), email
          delivery, hosting and database services, and, on the website and only with your consent, analytics (Meta,
          Google, TikTok). They process data only as needed to provide their services to us and under their own
          privacy terms.
        </p>
        <p>
          We may also disclose information when required by law or to protect the rights and safety of our users or
          the service.
        </p>
      </Section>

      <Section heading="5. Retention and account deletion" id="delete-account">
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-white">Account and planner data</strong> is kept while your account is active.</li>
          <li><strong className="text-white">Delete your account yourself:</strong> in the app, go to Settings → Delete account. This permanently deletes your account, your planner entries, and your active sign-in sessions.</li>
          <li><strong className="text-white">Request deletion by email:</strong> write to {mail} from the email on your account with the subject &ldquo;Delete my account&rdquo;. We will delete your account data, including any community posts you ask us to remove, within 30 days.</li>
          <li><strong className="text-white">Purchase records</strong> are kept as long as needed for access, support, and legal and accounting obligations.</li>
        </ul>
      </Section>

      <Section heading="6. Your choices and rights">
        <p>
          You can request access to, correction of, or deletion of your personal information, and you can unsubscribe
          from marketing emails using the link in any email. Depending on where you live, you may have additional
          rights under local law. Contact us to exercise them. We will not discriminate against you for exercising
          your rights.
        </p>
      </Section>

      <Section heading="7. Security">
        <p>
          We use encryption in transit (HTTPS) and access controls to protect your information. No method of
          transmission or storage is completely secure.
        </p>
      </Section>

      <Section heading="8. Children">
        <p>
          CommandCenterADHD is not directed to children under 13, and we do not knowingly collect their personal
          information. If you believe a child has given us information, contact us and we will delete it.
        </p>
      </Section>

      <Section heading="9. Changes to this policy">
        <p>
          If we make material changes, we will update the effective date above and, where appropriate, notify you by
          email or in the app.
        </p>
      </Section>

      <Section heading="10. Contact">
        <p>
          Eagle Africa Trading and Distribution Corporation
          <br />
          Charlotte, North Carolina, USA
          <br />
          Email: {mail}
        </p>
      </Section>

      <div className="h-16" aria-hidden="true" />
    </PageShell>
  );
}
